import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-unused-vars */
import { Button } from "@/shared/components/ui/button";
import React, { useState, useRef, useEffect } from "react";
// import ThinkingResponse from "./ThinkingResponse";
import ReactMarkdown from "react-markdown";
import GmailLogo from "@/assets/icons/gmailLogo.svg";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
// import SendBtn from "@/assets/icons/sendBtn.svg";
import { SendHorizontal } from "lucide-react";
import AssistantLogo from "@/assets/icons/assistant.svg";
import GoogleDriveIcon from "@/assets/icons/google-drive.svg";
import JiraIcon from "@/assets/icons/jiraLogo.svg";
// import PointerIcon from "@/assets/icons/square-mouse-pointer.svg";
import LoadingSpinner from "@/assets/icons/loadingSpinner.gif";
import "./../styles/style.css";
import { HOST_MODES } from "@/constants";
import MinimizedThinkingSteps from "./MinimizedThinkingSteps";
// import { useMainLayoutStore } from "@/shared/zustand/mainLayoutStore";
import AddToProjectButton from "./AddToProject";
// import useProjectStore from "@/shared/zustand/projectManagementStore";
import AudioControls from "./AudioControls";
import ImageTextUpload from "./ImageTextUpload";
import { LoadingMessage } from "./LoadingDotsChat";
import { useAudioStore } from "@/shared/zustand/audioStore";
// for Sanitization check while rendering html in chat
const schema = {
    ...defaultSchema,
    tagNames: [...(defaultSchema.tagNames || []), "iframe", "img", "video"],
    attributes: {
        ...defaultSchema.attributes,
        iframe: [
            "src",
            "width",
            "height",
            "allow",
            "allowfullscreen",
            "frameborder",
            "loading",
        ],
        img: ["src", "alt", "width", "height", "loading"],
        video: [
            "src",
            "width",
            "height",
            "controls",
            "autoplay",
            "loop",
            "muted",
            "playsinline",
        ],
    },
};
const ChatInterface = ({ onSendMessage, messages, isStreaming, handleArtifact, handleUploadData, handleAddToProject, initLoading, }) => {
    const textareaRef = useRef(null);
    const chatContainerRef = useRef(null);
    const [input, setInput] = useState("");
    const [isMobile, setIsMobile] = useState(false);
    const [isUserScrolling, setIsUserScrolling] = useState(false);
    const { audioState, setAudioState } = useAudioStore();
    // const [fontSettings, setFontSettings] = useState({
    //   font: null,
    //   size: 16,
    //   color: "000000",
    //   positionType: "automatic",
    //   position: "",
    // });
    // const [isFontAdded, setIsFontAdded] = useState(false);
    // const messagesEndRef = useRef<HTMLDivElement>(null);
    const isAtTop = () => {
        const container = chatContainerRef.current;
        if (!container)
            return true;
        const threshold = 5;
        // console.log("container.scrollTop",container.scrollTop)
        return container.scrollTop <= threshold;
    };
    const handleScroll = () => {
        if (chatContainerRef.current) {
            setIsUserScrolling(isAtTop());
        }
    };
    const scrollToDown = () => {
        const container = chatContainerRef.current;
        if (!container || isUserScrolling)
            return;
        container.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };
    const forceScrollToTop = () => {
        setIsUserScrolling(false);
        chatContainerRef.current?.scrollTo({
            top: 0,
            behavior: "auto",
        });
    };
    useEffect(() => {
        const container = chatContainerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
            return () => container.removeEventListener("scroll", handleScroll);
        }
    }, []);
    useEffect(() => {
        scrollToDown();
    }, [messages]);
    const handleSend = (latestInput) => {
        if (latestInput?.trim() || input.trim()) {
            textareaRef.current?.blur();
            onSendMessage(latestInput?.trim() || input);
            setInput("");
            forceScrollToTop();
            setAudioState((prev) => ({ ...prev, transcription: "" }));
        }
    };
    useEffect(() => {
        resizeCallback();
        function resizeCallback() {
            const width = window.innerWidth ||
                document.documentElement.clientWidth ||
                document.body.clientWidth;
            if (width < 1024) {
                setIsMobile(true);
            }
            else {
                setIsMobile(false);
            }
        }
        window.addEventListener("resize", resizeCallback);
        return () => window.removeEventListener("resize", resizeCallback);
    }, []);
    useEffect(() => {
        const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;
        if (!isTouchDevice) {
            !isMobile && textareaRef?.current?.focus();
        }
    }, [messages]);
    const formatDate = (date) => {
        const dateFormat = new Date(date);
        // Get hours and minutes
        let hours = dateFormat.getHours();
        const minutes = String(dateFormat.getMinutes()).padStart(2, "0");
        // Determine AM/PM and adjust hours
        const amPm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12; // Convert to 12-hour format
        // Return the formatted date with AM/PM
        return `${hours}:${minutes} ${amPm}`;
    };
    const processMessageContent = (content) => {
        try {
            // Parse the JSON content
            const message = JSON.parse(content);
            // console.log("processMessageContent", message);
            // Check the mode and return the appropriate response
            if (message.mode === "chat") {
                return message.widget; // Show widget message directly for "chat" mode
            }
            else if ("mode" in message &&
                message.mode === HOST_MODES["Feedback Mode"]) {
                return `Opening ${message.mode}`;
            }
            else if (message.widget !== undefined) {
                return `Opening the ${message.widget}`; // Format for other modes
            }
            else if (message.response !== undefined) {
                return message.response;
            }
        }
        catch (error) {
            // console.error("Error parsing message content:", error);
            return content;
        }
    };
    const handleArtifactClick = (artifact, fromInitialize, fromHost) => (event) => {
        event.preventDefault();
        handleArtifact(artifact, fromInitialize, fromHost);
    };
    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            if (e.shiftKey) {
                // Allow the default behavior for Shift+Enter (new line)
                return;
            }
            else {
                // Prevent the default enter behavior and send message
                e.preventDefault();
                if (input.trim()) {
                    handleSend();
                }
            }
        }
    };
    const handleInputChange = (e) => {
        setInput(e.target.value);
    };
    const truncateText = (text, maxWords) => {
        const words = text.split(" ");
        if (words.length > maxWords) {
            return words.slice(0, maxWords).join(" ") + "...";
        }
        return text;
    };
    return (_jsxs("div", { className: "h-full flex flex-col overflow-hidden rounded-b-[8px] font-content bg-[#FBFAFF]", children: [_jsx("div", { ref: chatContainerRef, className: "flex flex-col-reverse flex-1 px-5 pt-5 pb-0 overflow-y-auto chat-container-bg", children: _jsx("div", { className: "space-y-4", children: messages?.map((msg, index) => (_jsxs("div", { className: `flex flex-col ${msg.role === "user" ? "items-end" : "items-start"} gap-1`, children: [_jsxs("div", { className: `w-full flex ${msg.role === "user"
                                    ? "flex-row-reverse justify-start "
                                    : "flex-row justify-start"} items-start gap-3`, children: [msg.role !== "user" && (_jsx("div", { className: "flex justify-center items-center bg-[#FFF] text-[#8C8C8C] rounded-full", children: _jsx(AssistantLogo, {}) })), _jsxs("div", { className: `max-w-[90%] text-[1rem] flex flex-col gap-y-2 ${msg.role === "user"
                                            ? "p-4 bg-[#8694A10D] rounded-l-[8px] rounded-br-[8px]"
                                            : " text-[#344054] rounded-r-[8px] rounded-bl-[8px]"}`, children: [_jsxs("span", { className: `
                          ${msg.role === "user"
                                                    ? "text-[#344054] font-[500]"
                                                    : "bg-transparent "}
                        `, children: [msg.role === "assistant" &&
                                                        msg.orchestratorAndAgentsMessages &&
                                                        msg.orchestratorAndAgentsMessages.length > 0 && (_jsx(MinimizedThinkingSteps, { orchestratorAndAgentsMessages: msg?.orchestratorAndAgentsMessages, isStreamEnd: msg?.isStreamEnd })), _jsx(ReactMarkdown, { rehypePlugins: [rehypeRaw, [rehypeSanitize, schema]], children: msg.role === "assistant"
                                                            ? processMessageContent(msg.content) // Process only for assistant messages
                                                            : msg.content // Directly show the text for other roles
                                                     })] }), msg.role === "assistant" && msg?.artifactData && (_jsx("div", { children: (() => {
                                                    //drive artifact
                                                    if (msg.modeName === HOST_MODES["Drive Mode"]) {
                                                        return (_jsxs("div", { onClick: handleArtifactClick(msg.artifactData, false, true), className: "flex w-full flex-col items-start gap-4 p-4 transition-colors bg-white border border-[#D0D5DD] cursor-pointer rounded-xl hover:bg-gray-50", children: [_jsx("div", { className: "flex items-center justify-between w-full", children: _jsxs("div", { className: "flex items-center justify-center gap-2", children: [_jsx(GoogleDriveIcon, {}), _jsx("p", { className: "text-[16px] text-[#344054]", children: "Google Drive" })] }) }), _jsx("div", { className: "flex flex-col w-full", children: _jsxs("div", { className: "text-[#9CA3AF] text-sm", children: [msg?.artifactData?.artifactData?.length ?? 0, " ", "files.", " ", _jsx("span", { onClick: () => {
                                                                                    if (msg?.artifactData) {
                                                                                        handleArtifactClick(msg?.artifactData, false, true);
                                                                                    }
                                                                                }, className: "text-sm text-purple-600", children: msg?.artifactData?.artifactSubTitle ||
                                                                                    "click to view files" })] }) })] }));
                                                    }
                                                    //jira artifact
                                                    if (msg?.modeName === HOST_MODES["Jira Mode"]) {
                                                        return (_jsxs("div", { onClick: handleArtifactClick(msg.artifactData, false, true), className: "flex w-full flex-col items-start gap-4 p-4 transition-colors bg-white border border-[#D0D5DD] cursor-pointer rounded-xl hover:bg-gray-50", children: [_jsx("div", { className: "flex items-center justify-between w-full", children: _jsxs("div", { className: "flex items-center justify-center gap-2", children: [_jsx(JiraIcon, {}), _jsx("p", { className: "text-[16px] text-[#344054]", children: "Jira" })] }) }), _jsx("div", { className: "flex flex-col w-full", children: _jsxs("div", { className: "text-[#9CA3AF] text-sm", children: [msg?.artifactData?.artifactData?.length ?? 0, " ", "files.", " ", _jsx("span", { onClick: () => {
                                                                                    if (msg.artifactData) {
                                                                                        handleArtifactClick(msg.artifactData, false, true);
                                                                                    }
                                                                                }, className: "text-sm text-purple-600", children: msg?.artifactData?.artifactSubTitle ||
                                                                                    "click to view tickets" })] }) })] }));
                                                    }
                                                    //email artifact
                                                    // have to change this
                                                    else if (msg.modeName === HOST_MODES["Email Mode"]) {
                                                        return (_jsxs("div", { onClick: handleArtifactClick(msg.artifactData, false, true), className: "flex w-full flex-col items-start gap-4 p-4 transition-colors bg-white border border-[#D0D5DD] cursor-pointer rounded-xl hover:bg-gray-50", children: [_jsx("div", { className: "flex items-center justify-between w-full", children: _jsxs("div", { className: "flex items-center justify-center gap-2", children: [_jsx(GmailLogo, {}), _jsx("p", { className: "text-[16px] text-[#344054]", children: "Google Mail" })] }) }), _jsx("div", { className: "flex flex-col w-full", children: _jsxs("div", { className: "text-[#9CA3AF] text-sm", children: [msg?.artifactData?.artifactData?.length ?? 0, " ", "emails.", " ", _jsx("span", { onClick: () => {
                                                                                    if (msg.artifactData) {
                                                                                        handleArtifactClick(msg.artifactData, false, true);
                                                                                    }
                                                                                }, className: "text-sm text-purple-600", children: msg?.artifactData?.artifactSubTitle ||
                                                                                    "click to view emails" })] }) })] }));
                                                    }
                                                    //loading artifacts
                                                    else if (msg?.artifactData?.isLoading) {
                                                        return (_jsxs("div", { onClick: handleArtifactClick(msg.artifactData, false, false), className: "flex items-center gap-4 p-4 transition-colors bg-white border border-gray-100 cursor-pointer rounded-xl hover:bg-gray-50", children: [_jsx("div", { className: "p-3  rounded-full", children: _jsx("img", { className: "w-4 h-4", src: LoadingSpinner, alt: "Loading" }) }), _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "text-lg font-medium text-black", children: truncateText(msg.artifactData.artifactTitle, 5) }), _jsxs("div", { className: "flex items-center gap-x-3", children: [_jsx("span", { className: "text-sm text-purple-600", children: msg?.artifactData?.artifactSubTitle ||
                                                                                        "View more" }), (!("readyToShow" in msg.artifactData) ||
                                                                                    msg?.artifactData?.readyToShow ===
                                                                                        true) && (_jsx("div", { className: "border border-[#5928ed] rounded-full", children: _jsx(AddToProjectButton, { showIcon: true, showLabel: false, handleAddToProject: () => handleAddToProject(msg) }) }))] })] })] }));
                                                    }
                                                    //default case, normal artifact
                                                    else {
                                                        return (_jsxs("div", { onClick: handleArtifactClick(msg.artifactData, false, false), className: "w-full flex items-center gap-4 p-4 transition-colors bg-white border border-gray-100 cursor-pointer rounded-xl hover:bg-gray-50", children: [_jsx("div", { className: "p-3 bg-purple-100 rounded-full", children: msg?.artifactData?.artifactIcon ? (_jsx("img", { src: msg?.artifactData?.artifactIcon, alt: "loading", width: "24", height: "24" })) : (_jsxs("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", children: [_jsx("path", { d: "M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z", fill: "#7C3AED" }), _jsx("path", { d: "M14 17H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z", fill: "#7C3AED" })] })) }), _jsxs("div", { className: "flex flex-col", children: [_jsx("span", { className: "text-lg font-medium text-black", children: truncateText(msg?.artifactData?.artifactTitle, 5) }), _jsxs("div", { className: "flex items-center gap-x-3", children: [_jsx("span", { className: "text-sm text-purple-600", children: msg?.artifactData?.artifactSubTitle ||
                                                                                        "View more" }), (!("readyToShow" in msg?.artifactData) ||
                                                                                    msg?.artifactData?.readyToShow ===
                                                                                        true) && (_jsx("div", { className: "", children: _jsx(AddToProjectButton, { showIcon: true, showLabel: false, handleAddToProject: () => handleAddToProject(msg) }) }))] })] })] }));
                                                    }
                                                })() })), _jsx(ImageTextUpload, { msg: msg, handleUploadData: handleUploadData, messages: messages, index: index })] })] }), _jsx("div", { className: `text-[10px] text-[#A3A3A3] ${msg.role === "user" ? "pr-[0px]" : "pl-[3rem]"}`, children: msg?.time ? formatDate(msg?.time) : formatDate(Date.now()) })] }, index))) }) }), _jsxs("div", { className: "rounded-b-[8px] flex-col items-start flex-shrink-0 !px-5 !py-0", children: [_jsx("div", { className: "w-full flex items-center px-3 h-[28px]", children: isStreaming ? _jsx(LoadingMessage, {}) : "" }), _jsxs("div", { className: "flex w-full gap-2 bg-[#FFF] rounded-[8px] items-center border border-[#E5E7EB]", children: [_jsx("textarea", { ref: textareaRef, rows: 1, disabled: isStreaming, value: input, onChange: handleInputChange, onKeyDown: handleKeyDown, className: "flex-1 p-2 rounded-[8px] px-3 text-base text-[rgba(0, 51, 102, 0.60)] \n                     bg-[#FFF] placeholder:text-sm placeholder:font-normal focus:outline-none \n                     resize-none h-28 max-h-36 overflow-y-auto", placeholder: "How can I help?" }), _jsx(Button, { disabled: isStreaming || audioState.isRecording, title: "Click to send message", className: `rounded-full h-[40px] flex justify-center items-center gap-x-1 text-[white] bg-transparent shadow-none self-end mb-1 ${audioState.isRecording ? "cursor-not-allowed" : ""}`, onClick: () => handleSend(), children: _jsx(SendHorizontal, { color: input.length > 0 && !audioState.isRecording
                                        ? `#5928ed`
                                        : "#d1d5db", size: 20 }) })] }), _jsx("div", { className: " w-full h-16", children: _jsx(AudioControls, { handleSend: handleSend, setInput: setInput, initLoading: initLoading, className: " w-full h-full" }) })] })] }));
};
export default ChatInterface;
